import { createContext, type ReactNode, useContext, useState } from 'react';
import { cn } from './cn';
import { segmentedItem, segmentedTrack } from './segmented';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

/**
 * Uncontrolled with `defaultValue`, controlled with `value` + `onValueChange`.
 *
 * It was uncontrolled only, and that is not a missing convenience — it is the
 * reason a tab could not be linked to. Which ranking you were reading on
 * Eco-driving, which section of Settings you were in: all of it was reachable
 * only by arriving at the page and clicking, lost on reload, and impossible to
 * put in a URL because the component would not accept one. Making it
 * controllable is what lets a route own the choice; see `lib/urlState.ts` in
 * the app for which kinds of state belong there.
 *
 * Both modes, rather than controlled only, because most tab strips in this
 * product genuinely are local — a form's sections are not a question about a
 * fleet, and forcing every one of them through a route would put noise in the
 * address bar to no end.
 */
interface TabsProps {
  /** Uncontrolled: the tab to open with. Ignored when `value` is given. */
  defaultValue?: string;
  /** Controlled: the open tab. Pass `onValueChange` with it. */
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? '');
  const isControlled = value !== undefined;
  const activeTab = isControlled ? value : uncontrolled;

  // A controlled strip still updates its own copy, so a caller may pass
  // `value` without `onValueChange` for a tab that is read-only for now
  // without the strip freezing.
  const setActiveTab = (next: string) => {
    if (!isControlled) setUncontrolled(next);
    onValueChange?.(next);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className }: TabsListProps) {
  // The strip used to be `bg-muted`, which is oklch(0.968) against a page at
  // 0.965 — invisible on its own, so the tabs read as loose text scattered
  // above the content and the border was doing all the work. The track is cut
  // into the page now; see `segmented.ts`.
  return (
    <div className={segmentedTrack('md', className)} role="tablist">
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
}

export function TabsTrigger({ value, children, className, icon }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabs();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => setActiveTab(value)}
      // Lifted off the track rather than filled with the brand. The fill was
      // itself a fix for an earlier version where active was `bg-background` on
      // a `bg-muted` strip, 0.003 apart in lightness — it separated the states
      // by shouting instead of by moving the ground they sit on, which is what
      // `--surface-sunken` now does. It survives the slate palette for the same
      // reason the fill did, and without depending on the brand at all.
      className={segmentedItem(isActive, 'md', className)}
    >
      {icon}
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeTab } = useTabs();

  if (activeTab !== value) return null;

  return (
    <div role="tabpanel" className={cn('mt-4', className)}>
      {children}
    </div>
  );
}
